import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const watchEntityKeys = ['fontFamilies']

const useFontFamilies = (options = {}) => {
  const { filters } = options
  const { string: filterString } = filters || {}

  const {
    updatedEntities: { fontFamilies: fontFamiliesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const fontFamilies = useSelector(reduxState => reduxState.entities.fontFamilies)

  const filteredFontFamilies = useMemo(() => {
    const filtered = Object.values(fontFamilies).filter((fontFamily) => {
      const { id, title } = fontFamily

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [fontFamiliesUpdatedAt, JSON.stringify(filters)])

  const filteredFontFamiliesCount = filteredFontFamilies.length
  const hasFilteredFontFamilies = !!filteredFontFamiliesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredFontFamiliesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingFontFamilies } = useReduxAction(
    'fontFamilies',
    'loadFontFamilies',
    {
      ...defaultRequestOptions.fontFamily,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredFontFamilies,
    filteredFontFamiliesCount,
    hasFilteredFontFamilies,
    loading: loadingFontFamilies,
  }
}

export default useFontFamilies
